import { faSquareInfo } from "@fortawesome/pro-regular-svg-icons"
import { Button, Typography } from "@mui/material"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { messageSVC } from "message/service"
import { NotAccessiblePhoneScreenPage } from "page/components/NotAccessiblePhoneScreenPage"
import { NotFoundPage } from "page/components/NotFoundPage"
import React from "react"
import { connect } from "react-redux"
import { useParams } from "react-router-dom"
import { IReportForm, IUserReport, REPORT_CREATE_HEADER_HEIGHT_IN_REM } from "reports/model"
import { getDefaultReportParameters } from "reports/store/state"
import {
  clearReportsParameters,
  getReportParametersFromUserReport,
  hasReportSearchChanged,
  loadReportForm,
  updateUserReport
} from "reports/tools/common"
import { getReportForm } from "reports/tools/ui"
import { FlexBox } from "ui/components/FlexBox"
import { LinearLoader } from "ui/components/LinearLoader"
import { CreateReportPopup } from "./CreateReportPopup"
import { ListMessage } from "./ListMessage"
import { ReportParametersMenu } from "./ReportParametersMenu"
import { ReportsResultList } from "./ReportsResultList"

interface IReportFormPageProps {
  isCreation: boolean
}

interface IReportFormPageConnectProps {
  reportForm: IReportForm
  isScreenLargeEnough: boolean
  isListLoading: boolean
}

const ReportFormPageFn = React.memo(
  ({
    isCreation,
    reportForm: { parameters, hasResultLoadingError, isResultLoading, isUpdating, report, updateFormError },
    isListLoading,
    isScreenLargeEnough
  }: IReportFormPageProps & IReportFormPageConnectProps) => {
    const [isCreatePopupOpened, setIsCreatePopupOpened] = React.useState(false)
    if (!isCreation) {
      const { reportId } = useParams()
      React.useEffect(() => {
        if (typeof reportId !== "string" || reportId === "") {
          console.error("Unable to find report id in url")
          return
        }
        loadReportForm(Number(reportId))
      }, [])
    }
    const hasChange = hasReportSearchChanged(
      parameters,
      isCreation || report === null ? getDefaultReportParameters() : getReportParametersFromUserReport(report)
    )
    if (!isScreenLargeEnough) {
      return <NotAccessiblePhoneScreenPage />
    }
    if (!isCreation) {
      if (isListLoading) {
        return <LinearLoader />
      } else if (!report) {
        return <NotFoundPage />
      }
    }
    return (
      <FlexBox vertical fullWidth fullHeight jc="start">
        <FlexBox
          fullWidth
          jc="space-between"
          sx={{
            pl: "3rem",
            pr: "1rem",
            height: REPORT_CREATE_HEADER_HEIGHT_IN_REM,
            minHeight: REPORT_CREATE_HEADER_HEIGHT_IN_REM,
            maxHeight: REPORT_CREATE_HEADER_HEIGHT_IN_REM,
            borderBottom: theme => theme.palette.border
          }}>
          <Typography
            id="reports-header-title"
            color="textPrimary"
            fontSize="1.1rem"
            fontWeight={500}
            textTransform="uppercase">
            {translate(isCreation ? "reports.create.title" : "reports.edit.page.title")}
          </Typography>
          <FlexBox gap>
            <Button
              id="reports-cancel-button"
              variant="outlined"
              disabled={isUpdating || isResultLoading || !hasChange}
              onClick={() => clearReportsParameters(isCreation)}>
              {translate("button.cancel")}
            </Button>
            <Button
              id="reports-save-button"
              variant="contained"
              disabled={
                isUpdating ||
                isResultLoading ||
                !hasChange ||
                hasResultLoadingError ||
                parameters.groupBys.length === 0 ||
                !!updateFormError
              }
              onClick={() => {
                if (isCreation) {
                  setIsCreatePopupOpened(true)
                } else {
                  messageSVC.confirmMessage({
                    title: translate("reports.update.title"),
                    message: translate("reports.update.confirm.message", (report as IUserReport).name),
                    addGenericConfirmMessage: true,
                    confirmButtonLabel: translate("label.update"),
                    onSuccess: () => updateUserReport({ ...(report as IUserReport), ...parameters })
                  })
                }
              }}>
              {translate(isCreation ? "button.save" : "button.update")}
            </Button>
          </FlexBox>
        </FlexBox>
        <FlexBox fullWidth fullHeight={REPORT_CREATE_HEADER_HEIGHT_IN_REM} ai="start">
          {parameters.groupBys.length === 0 ? (
            <ListMessage
              message={translate("reports.groupBy.must.select")}
              icon={faSquareInfo}
              sx={{ mt: "2rem", width: "100%" }}
            />
          ) : (
            <ReportsResultList isCreation={isCreation} />
          )}
          <ReportParametersMenu isCreation={isCreation} />
        </FlexBox>
        {isCreatePopupOpened && (
          <CreateReportPopup parameters={parameters} onClose={() => setIsCreatePopupOpened(false)} />
        )}
      </FlexBox>
    )
  }
)

export const ReportFormPage = connect(
  (state: IRootState, ownProps: IReportFormPageProps): IReportFormPageConnectProps => ({
    reportForm: getReportForm(ownProps.isCreation, state.reports),
    isListLoading: state.reports.isListLoading,
    isScreenLargeEnough: state.ui.contentWidth > 900
  })
)(ReportFormPageFn)
